import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Alert,
  Button,
  Empty,
  Input,
  Modal,
  Select,
  Spin,
  Tooltip,
  Typography,
} from "antd";

import {
  ArrowLeftOutlined,
  BookOutlined,
  CheckOutlined,
  HomeOutlined,
  RightOutlined,
  EyeOutlined,
  FilePdfOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";

import { useNavigate } from "react-router-dom";

const {
  Title,
  Text,
} = Typography;

const API_ROOT =
  process.env.REACT_APP_EXAM_API_BASE_URL ||
  "http://localhost:5000/api/exams";

const API_ORIGIN =
  API_ROOT.replace(/\/api\/exams\/?$/, "");

const SOURCE_OPTIONS = {
  SMART: "smart",
  TRAKWEL: "trackwel",
};

const formatFileSize = (
  bytes
) => {
  const value =
    Number(bytes || 0);

  if (!value) {
    return "-";
  }

  if (value < 1024) {
    return `${value} B`;
  }

  if (
    value <
    1024 * 1024
  ) {
    return `${(
      value / 1024
    ).toFixed(1)} KB`;
  }

  return `${(
    value /
    (1024 * 1024)
  ).toFixed(1)} MB`;
};

const formatDate = (
  value
) => {
  if (!value) {
    return "-";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return value;
  }

  return date.toLocaleDateString(
    "en-IN",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }
  );
};

const getAbsoluteUrl = (
  url
) => {
  if (!url) {
    return "";
  }

  if (
    /^https?:\/\//i.test(
      url
    )
  ) {
    return url;
  }

  const normalized =
    url.startsWith("/")
      ? url
      : `/${url}`;

  return `${API_ORIGIN}${normalized}`;
};

const parseJsonSafely =
  async (response) => {
    const raw =
      await response.text();

    if (!raw) {
      return {
        status: response.ok,
        message:
          response.ok
            ? "Request completed"
            : "Request failed",
      };
    }

    try {
      return JSON.parse(raw);
    } catch {
      console.error(
        "Training material API returned non-JSON response:",
        raw
      );

      return {
        status: false,
        message:
          `Server returned an invalid response (${response.status})`,
      };
    }
  };

const TrainingMaterialPage =
  () => {
    const navigate =
      useNavigate();

    const [
      source,
      setSource,
    ] =
      useState(
        SOURCE_OPTIONS.SMART
      );

    const [
      departments,
      setDepartments,
    ] =
      useState([]);

    const [
      departmentId,
      setDepartmentId,
    ] =
      useState(null);

    const [
      materials,
      setMaterials,
    ] =
      useState([]);

    const [
      search,
      setSearch,
    ] =
      useState("");

    const [
      loadingDepartments,
      setLoadingDepartments,
    ] =
      useState(false);

    const [
      loadingMaterials,
      setLoadingMaterials,
    ] =
      useState(false);

    const [
      error,
      setError,
    ] =
      useState("");

    const [
      previewOpen,
      setPreviewOpen,
    ] =
      useState(false);

    const [
      previewMaterial,
      setPreviewMaterial,
    ] =
      useState(null);

    const [
      previewUrl,
      setPreviewUrl,
    ] =
      useState("");

    const [
      previewLoading,
      setPreviewLoading,
    ] =
      useState(false);

    const [
      previewError,
      setPreviewError,
    ] =
      useState("");

    const selectedDepartment =
      useMemo(
        () =>
          departments.find(
            (item) =>
              Number(
                item.department_id
              ) ===
              Number(
                departmentId
              )
          ) || null,
        [
          departments,
          departmentId,
        ]
      );

    const filteredMaterials =
      useMemo(
        () => {
          const keyword =
            String(
              search || ""
            )
              .trim()
              .toLowerCase();

          if (!keyword) {
            return materials;
          }

          return materials.filter(
            (item) => {
              const haystack =
                [
                  item.material_title,
                  item.file_name,
                ]
                  .filter(Boolean)
                  .join(" ")
                  .toLowerCase();

              return haystack.includes(
                keyword
              );
            }
          );
        },
        [
          materials,
          search,
        ]
      );

    const fetchDepartments =
      async (
        selectedSource
      ) => {
        setLoadingDepartments(
          true
        );

        setError("");

        setDepartments(
          []
        );

        setDepartmentId(
          null
        );

        setMaterials(
          []
        );

        try {
          const response =
            await fetch(
              `${API_ROOT}/training-materials/departments?source=${encodeURIComponent(
                selectedSource
              )}`
            );

          const body =
            await parseJsonSafely(
              response
            );

          if (
            !response.ok ||
            !body?.status
          ) {
            throw new Error(
              body?.message ||
                "Unable to load departments"
            );
          }

          const rows =
            Array.isArray(
              body?.data
            )
              ? body.data
              : [];

          setDepartments(
            rows
          );
        } catch (
          err
        ) {
          console.error(
            "Training departments:",
            err
          );

          setError(
            err?.message ||
              "Unable to load departments"
          );
        } finally {
          setLoadingDepartments(
            false
          );
        }
      };

    const fetchMaterials =
      async (
        selectedSource,
        selectedDepartmentId
      ) => {
        if (
          !selectedDepartmentId
        ) {
          setMaterials(
            []
          );

          return;
        }

        setLoadingMaterials(
          true
        );

        setError("");

        try {
          const params =
            new URLSearchParams({
              source:
                selectedSource,

              department_id:
                String(
                  selectedDepartmentId
                ),

              status:
                "active",

              page:
                "1",

              limit:
                "100",
            });

          const response =
            await fetch(
              `${API_ROOT}/training-materials?${params.toString()}`
            );

          const body =
            await parseJsonSafely(
              response
            );

          if (
            !response.ok ||
            !body?.status
          ) {
            throw new Error(
              body?.message ||
                "Unable to load training materials"
            );
          }

          setMaterials(
            Array.isArray(
              body?.data
            )
              ? body.data
              : []
          );
        } catch (
          err
        ) {
          console.error(
            "Training materials:",
            err
          );

          setMaterials(
            []
          );

          setError(
            err?.message ||
              "Unable to load training materials"
          );
        } finally {
          setLoadingMaterials(
            false
          );
        }
      };

    useEffect(() => {
      fetchDepartments(
        source
      );
    }, [source]);

    useEffect(() => {
      if (
        departmentId
      ) {
        fetchMaterials(
          source,
          departmentId
        );
      }
    }, [
      source,
      departmentId,
    ]);

    useEffect(
      () => {
        return () => {
          if (
            previewUrl &&
            previewUrl.startsWith(
              "blob:"
            )
          ) {
            URL.revokeObjectURL(
              previewUrl
            );
          }
        };
      },
      [
        previewUrl,
      ]
    );

    const handleSourceChange =
      (
        nextSource
      ) => {
        if (
          nextSource ===
          source
        ) {
          return;
        }

        setSearch(
          ""
        );

        setPreviewMaterial(
          null
        );

        setPreviewOpen(
          false
        );

        setPreviewError(
          ""
        );

        setPreviewLoading(
          false
        );

        setPreviewUrl(
          (current) => {
            if (
              current &&
              current.startsWith(
                "blob:"
              )
            ) {
              URL.revokeObjectURL(
                current
              );
            }

            return "";
          }
        );

        setSource(
          nextSource
        );
      };

    const closePreview =
      () => {
        setPreviewOpen(
          false
        );

        setPreviewMaterial(
          null
        );

        setPreviewLoading(
          false
        );

        setPreviewError(
          ""
        );

        setPreviewUrl(
          (current) => {
            if (
              current &&
              current.startsWith(
                "blob:"
              )
            ) {
              URL.revokeObjectURL(
                current
              );
            }

            return "";
          }
        );
      };

    const handlePreview =
      async (
        material
      ) => {
        if (!material) {
          return;
        }

        setPreviewMaterial(
          material
        );

        setPreviewOpen(
          true
        );

        setPreviewLoading(
          true
        );

        setPreviewError(
          ""
        );

        setPreviewUrl(
          (current) => {
            if (
              current &&
              current.startsWith(
                "blob:"
              )
            ) {
              URL.revokeObjectURL(
                current
              );
            }

            return "";
          }
        );

        try {
          const url =
            getAbsoluteUrl(
              material.view_url
            );

          if (!url) {
            throw new Error(
              "PDF preview URL is unavailable"
            );
          }

          const response =
            await fetch(
              url,
              {
                method:
                  "GET",

                headers: {
                  Accept:
                    "application/pdf",
                },
              }
            );

          if (!response.ok) {
            throw new Error(
              `Unable to load PDF (${response.status})`
            );
          }

          const blob =
            await response.blob();

          if (
            !blob ||
            blob.size === 0
          ) {
            throw new Error(
              "PDF file is empty"
            );
          }

          const pdfBlob =
            blob.type ===
            "application/pdf"
              ? blob
              : new Blob(
                  [blob],
                  {
                    type:
                      "application/pdf",
                  }
                );

          setPreviewUrl(
            URL.createObjectURL(
              pdfBlob
            )
          );
        } catch (
          err
        ) {
          console.error(
            "Training material preview:",
            err
          );

          setPreviewError(
            err?.message ||
            "Unable to preview this PDF"
          );
        } finally {
          setPreviewLoading(
            false
          );
        }
      };

    const refresh =
      () => {
        if (
          departmentId
        ) {
          fetchMaterials(
            source,
            departmentId
          );
        } else {
          fetchDepartments(
            source
          );
        }
      };

    const sourceTitle =
      source ===
      SOURCE_OPTIONS.SMART
        ? "SM-ART"
        : "Trakwel";

    return (
      <div className="training-material-page">
        <header className="training-material-topbar">
          <div className="training-material-topbar-inner">
            <div className="training-material-brand">
              <img
                src="/logo192.png"
                alt="SM-ART"
                className="training-material-brand-logo"
              />

              <div className="training-material-brand-copy">
                <strong>
                  Training & Assessment
                </strong>

                <span>
                  Learning Portal
                </span>
              </div>
            </div>

            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
              className="training-material-back"
            >
              Back
            </Button>
          </div>
        </header>

        <main className="training-material-main">
          <section className="training-material-hero">
            <div className="training-material-hero-pattern" />

            <div className="training-material-hero-content">
              <div className="training-material-hero-copy">
                <div className="training-material-breadcrumb">
                  <span>
                    <HomeOutlined />
                    Assessment
                  </span>

                  <RightOutlined />

                  <strong>
                    Training Material
                  </strong>
                </div>

                <div className="training-material-hero-heading">
                  <span className="training-material-hero-icon">
                    <BookOutlined />
                  </span>

                  <div>
                    <Title level={2}>
                      Learning Resources
                    </Title>

                    <Text>
                      Choose a system and department to access approved training PDFs.
                    </Text>
                  </div>
                </div>
              </div>

              <Tooltip title="Refresh materials">
                <Button
                  icon={<ReloadOutlined />}
                  onClick={refresh}
                  loading={
                    loadingDepartments ||
                    loadingMaterials
                  }
                  className="training-material-refresh"
                >
                  Refresh
                </Button>
              </Tooltip>
            </div>
          </section>

          <section className="training-material-source-section">

            <div className="training-material-panel-head">
              <span className="training-material-step-badge">
                01
              </span>

              <div className="training-material-panel-copy">
                <strong>
                  Choose Training Source
                </strong>

                <span>
                  Select a platform, then choose the department to view its training materials.
                </span>
              </div>
            </div>

            <div className="training-material-source-selector">
              <button
                type="button"
                className={
                  source ===
                  SOURCE_OPTIONS.SMART
                    ? "training-material-source-option is-active"
                    : "training-material-source-option"
                }
                onClick={() =>
                  handleSourceChange(
                    SOURCE_OPTIONS.SMART
                  )
                }
              >
                <span className="training-material-source-mark">
                  S
                </span>

                <span>
                  <strong>
                    SM-ART
                  </strong>

                  <small>
                    Smart Manufacturing
                  </small>
                </span>

                {source === SOURCE_OPTIONS.SMART && (
                  <span className="training-material-source-check">
                    <CheckOutlined />
                  </span>
                )}
              </button>

              <button
                type="button"
                className={
                  source ===
                  SOURCE_OPTIONS.TRAKWEL
                    ? "training-material-source-option is-active"
                    : "training-material-source-option"
                }
                onClick={() =>
                  handleSourceChange(
                    SOURCE_OPTIONS.TRAKWEL
                  )
                }
              >
                <span className="training-material-source-mark">
                  T
                </span>

                <span>
                  <strong>
                    Trakwel
                  </strong>

                  <small>
                    Training Resources
                  </small>
                </span>

                {source === SOURCE_OPTIONS.TRAKWEL && (
                  <span className="training-material-source-check">
                    <CheckOutlined />
                  </span>
                )}
              </button>
            </div>

            <div className="training-material-filter-divider" />

            <div className="training-material-filter-row">
              <div className="training-material-filter-field">
                <label>
                  Department
                </label>

                <Select
                  value={
                    departmentId
                  }
                  onChange={
                    setDepartmentId
                  }
                  loading={
                    loadingDepartments
                  }
                  disabled={
                    loadingDepartments
                  }
                  placeholder={
                    `Select ${sourceTitle} department`
                  }
                  showSearch
                  allowClear
                  optionFilterProp="searchText"
                  filterOption={(
                    input,
                    option
                  ) =>
                    String(
                      option?.searchText ||
                      ""
                    )
                      .toLowerCase()
                      .includes(
                        String(
                          input ||
                          ""
                        ).toLowerCase()
                      )
                  }
                  popupClassName="training-material-department-dropdown"
                  optionLabelProp="selectedLabel"
                  options={
                    departments.map(
                      (
                        department
                      ) => {
                        const deptId =
                          department.department_id;

                        const deptName =
                          department.department_name ||
                          `Department ${deptId}`;

                        const departmentLabel =
                          `${deptName} · Dept ID: ${deptId}`;

                        return {
                          value:
                            Number(
                              deptId
                            ),

                          selectedLabel:
                            departmentLabel,

                          searchText:
                            `${deptName} ${deptId}`,

                          label:
                            departmentLabel,
                        };
                      }
                    )
                  }
                  onClear={() => {
                    setDepartmentId(
                      null
                    );

                    setMaterials(
                      []
                    );
                  }}
                />
              </div>

              <div className="training-material-filter-field">
                <label>
                  Search Material
                </label>

                <Input
                  value={
                    search
                  }
                  onChange={(
                    event
                  ) =>
                    setSearch(
                      event.target.value
                    )
                  }
                  prefix={
                    <SearchOutlined />
                  }
                  placeholder="Search PDF title or file name"
                  disabled={
                    !departmentId
                  }
                  allowClear
                />
              </div>
            </div>
          </section>

          {error && (
            <Alert
              type="error"
              showIcon
              message={
                error
              }
              className="training-material-alert"
            />
          )}

          <section className="training-material-list-section">
            <div className="training-material-list-head">
              <div className="training-material-list-title">
                <span className="training-material-list-icon">
                  <FilePdfOutlined />
                </span>

                <div>
                  <strong>
                    Training Materials
                  </strong>

                <span>
                  {selectedDepartment
                    ? `${
                        selectedDepartment.department_name ||
                        `Department ${selectedDepartment.department_id}`
                      } • Dept ID: ${
                        selectedDepartment.department_id
                      } • ${sourceTitle}`
                    : "Choose a department to view available PDFs"}
                  </span>
                </div>
              </div>

              {departmentId && (
                <span className="training-material-count">
                  {filteredMaterials.length} PDF
                  {filteredMaterials.length === 1
                    ? ""
                    : "s"}
                </span>
              )}
            </div>

            {loadingMaterials ? (
              <div className="training-material-loading">
                <Spin />

                <span>
                  Loading training materials...
                </span>
              </div>
            ) : !departmentId ? (
              <Empty
                image={
                  Empty.PRESENTED_IMAGE_SIMPLE
                }
                description="Select a department to continue"
                className="training-material-empty"
              />
            ) : filteredMaterials.length ===
              0 ? (
              <Empty
                image={
                  Empty.PRESENTED_IMAGE_SIMPLE
                }
                description={
                  search
                    ? "No material matches your search"
                    : "No training material available for this department"
                }
                className="training-material-empty"
              />
            ) : (
              <div className="training-material-list">
                <div className="training-material-table-head">
                  <span>
                    Material
                  </span>

                  <span>
                    File
                  </span>

                  <span>
                    Size
                  </span>

                  <span>
                    Uploaded
                  </span>

                  <span>
                    Actions
                  </span>
                </div>

                {filteredMaterials.map(
                  (
                    material
                  ) => (
                    <div
                      key={
                        material.material_id
                      }
                      className="training-material-row"
                    >
                      <div className="training-material-name">
                        <span className="training-material-pdf-icon">
                          <FilePdfOutlined />
                        </span>

                        <div>
                          <strong>
                            {material.material_title ||
                              "Training Material"}
                          </strong>

                          <small>
                            PDF Document
                          </small>
                        </div>
                      </div>

                      <div className="training-material-cell">
                        <span className="training-material-mobile-label">
                          File
                        </span>

                        <strong>
                          {material.file_name ||
                            "-"}
                        </strong>
                      </div>

                      <div className="training-material-cell">
                        <span className="training-material-mobile-label">
                          Size
                        </span>

                        <strong>
                          {formatFileSize(
                            material.file_size
                          )}
                        </strong>
                      </div>

                      <div className="training-material-cell">
                        <span className="training-material-mobile-label">
                          Uploaded
                        </span>

                        <strong>
                          {formatDate(
                            material.created_at
                          )}
                        </strong>
                      </div>

                      <div className="training-material-actions">
                        <Tooltip title="Preview PDF">
                          <Button
                            icon={
                              <EyeOutlined />
                            }
                            onClick={() =>
                              handlePreview(
                                material
                              )
                            }
                            className="training-material-action-button training-material-view-button"
                          >
                            View
                          </Button>
                        </Tooltip>
                      </div>
                    </div>
                  )
                )}
              </div>
            )}
          </section>
        </main>

        <Modal
          open={
            previewOpen
          }
          title={
            <div className="training-material-preview-title-wrap">
              <strong className="training-material-preview-title-text">
                {previewMaterial?.material_title ||
                  previewMaterial?.file_name ||
                  "Training Material"}
              </strong>

              <div className="training-material-preview-title-meta">
                <span>
                  {sourceTitle}
                </span>

                <span>
                  {selectedDepartment?.department_name || "-"}
                </span>

                <span>
                  Dept {selectedDepartment?.department_id || "-"}
                </span>

                <span>
                  {previewMaterial?.file_name || "-"}
                </span>

                <span>
                  {formatFileSize(previewMaterial?.file_size)}
                </span>
              </div>
            </div>
          }
          onCancel={
            closePreview
          }
          footer={null}
          width="100vw"
          centered={false}
          style={{
            top: 0,
            paddingBottom: 0,
            margin: 0,
            maxWidth: "100vw",
          }}
          destroyOnClose
          className="training-material-preview-modal"
          rootClassName="training-material-preview-root"
        >
          <div className="training-material-preview-body">

            {previewLoading ? (
              <div className="training-material-preview-state">
                <Spin
                  size="large"
                />

                <strong>
                  Loading PDF...
                </strong>

                <span>
                  Preparing the document preview.
                </span>
              </div>
            ) : previewError ? (
              <div className="training-material-preview-state is-error">
                <FilePdfOutlined />

                <strong>
                  Preview unavailable
                </strong>

                <span>
                  {previewError}
                </span>
              </div>
            ) : previewUrl ? (
              <iframe
                src={
                  `${previewUrl}#view=FitH&navpanes=0`
                }
                title={
                  previewMaterial?.material_title ||
                  "Training Material PDF"
                }
                className="training-material-preview-frame"
              />
            ) : (
              <Empty
                description="Preview unavailable"
              />
            )}
          </div>
        </Modal>

      </div>
    );
  };

export default TrainingMaterialPage;
